import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillChip, InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, Save } from "lucide-react";

// iter198 · Owner-Vorgabe: Altersbereiche + Gender-Pflichtfeld für STX-Steckbrief
// iter198e (Follow-up): Codes werden über t() lokalisiert — keine hardgecodeten UI-Strings mehr.
const AGE_RANGES = ["18-25", "26-35", "36-45", "46-55", "56-65", "65+"];
const BODY_TYPE_CODES = ["SLIM", "SPORTY", "NORMAL", "CURVY", "STRONG"];
const GENDER_CODES = ["FEMALE", "MALE"];
// Legacy-DE-Labels aus DB → Code-Migration (bevor Save)
const BODY_TYPE_LEGACY_TO_CODE = {
  "Schlank": "SLIM", "Sportlich": "SPORTY", "Normal": "NORMAL",
  "Kurvig": "CURVY", "Kräftig": "STRONG",
};
// Legacy-Werte migrieren (alte Bereiche → neue Bereiche)
function migrateAgeRange(r) {
  const legacyMap = { "18-22": "18-25", "23-27": "26-35", "28-35": "26-35", "46+": "46-55", "56+": "56-65" };
  return legacyMap[r] || r;
}

export default function FacetEditorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/profile/facets");
        // iter172 · Legacy-Age-Range migrieren
        const migrated = { ...(data || {}) };
        if (migrated.age_range) migrated.age_range = migrateAgeRange(migrated.age_range);
        setFacets(migrated);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [user, navigate]);

  const saveFacets = async () => {
    setError(""); setSuccess(""); setSaving(true);
    try {
      const { data } = await api.post("/profile/facets", {
        gender: facets.gender || null,
        age_range: facets.age_range || null,
        height_cm: facets.height_cm ? parseInt(facets.height_cm) : null,
        weight_kg: facets.weight_kg ? parseInt(facets.weight_kg) : null,
        body_type: facets.body_type || null,
        shoe_size: facets.shoe_size || null,
        hair_color: facets.hair_color || null,
      });
      setFacets(data);
      setSuccess(t("facets.saved"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(formatError(err)); }
    finally { setSaving(false); }
  };

  // iter198 · „einmal speicherbar" — sobald filled_at gesetzt ist,
  // sind Gender + AgeRange bis zur AVS-Verifizierung persistent.
  const locked = !!(facets && facets._locked_until_avs);
  // iter212 · Rest-Felder 180-Tage-Cooldown. Wenn `_other_fields_editable=false`
  // sind bereits gesetzte Rest-Werte gesperrt (nachtragen leerer Felder bleibt
  // backend-seitig erlaubt — hier aber der UI-Klarheit halber ebenfalls gesperrt).
  const otherFieldsEditable = facets?._other_fields_editable !== false;
  const nextChangeIso = facets?._other_fields_next_change_at;
  const nextChangeLabel = nextChangeIso
    ? new Date(nextChangeIso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="facet-editor-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="facet-back" onClick={() => navigate("/profile")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A] flex-1" data-testid="facet-title">{t("facet_editor.title", { defaultValue: "stilltouchedX Steckbrief" })}</h1>
      </div>

      <div className="px-4 py-4 space-y-5">
        <p className="text-xs text-[#999] leading-relaxed">
          {t("facet_editor.intro", { defaultValue: "Dein stilltouchedX Steckbrief ist Pflicht wenn du im STX-Bereich verkaufen möchtest. Er hilft Käufern deine Artikel besser einzuschätzen (Größe, Alter, Statur)." })}
        </p>

        {/* iter198 · AVS-Hinweis (Owner-Vorgabe 2026-02-28) */}
        <InfoCard variant="warning" size="sm" testId="facet-avs-hint" className="rounded">
          {t("facet_editor.avs_hint", { defaultValue: "Wichtig: Geschlechts- und Altersbereichs-Angaben werden nach deiner AVS-Verifizierung automatisch abgeglichen. Bei Abweichung ersetzt AVS deine manuellen Werte. Du kannst diese beiden Felder nur einmalig manuell setzen — eine spätere Änderung ist nur über die AVS-Verifizierung möglich." })}
        </InfoCard>

        {locked && (
          <InfoCard variant="info" size="sm" testId="facet-locked-hint" className="rounded">
            {t("facet_editor.locked_hint", { defaultValue: "Dein Steckbrief ist bereits gespeichert. Produktverkauf (Damen/Herren) und Altersbereich können nicht angepasst werden. Größe, Gewicht, Statur, Schuhgröße und Haarfarbe kannst du weiterhin selbst aktualisieren. Eine Änderung ist alle 180 Tage möglich." })}
          </InfoCard>
        )}

        {locked && nextChangeLabel && (
          <div
            data-testid="facet-cooldown-countdown"
            data-editable={otherFieldsEditable ? "1" : "0"}
            className={`p-3 rounded text-[11px] ${otherFieldsEditable ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-orange-50 border border-orange-200 text-orange-900"}`}
          >
            {otherFieldsEditable
              ? t("facet_editor.cooldown_editable_now", {
                  defaultValue: "Größe, Gewicht, Statur, Schuhgröße und Haarfarbe können jetzt angepasst werden. Nach der nächsten Änderung sind sie für 180 Tage gesperrt.",
                })
              : t("facet_editor.cooldown_blocked", {
                  defaultValue: "Größe, Gewicht, Statur, Schuhgröße und Haarfarbe sind derzeit gesperrt. Nächste Anpassung möglich am {{date}}.",
                  date: nextChangeLabel,
                })}
          </div>
        )}

        {error && <InfoCard variant="danger" testId="facet-error">{error}</InfoCard>}
        {success && <InfoCard variant="success" testId="facet-success">{success}</InfoCard>}

        {/* iter198 · Gender-Pflichtfeld (Damen/Herren) */}
        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">
            {t("facets.gender", { defaultValue: "Produktverkauf" })}
          </label>
          <div className="flex gap-2 mt-2 flex-wrap" data-testid="facet-gender-group">
            {GENDER_CODES.map((code) => (
              <PillChip key={code} testId={`facet-gender-${code.toLowerCase()}`}
                active={facets.gender === code}
                disabled={locked}
                onClick={() => { if (!locked) setFacets(prev => ({ ...prev, gender: code })); }}
                variant="neutral" size="md">
                {t(`facets.gender_options.${code}`, { defaultValue: code === "FEMALE" ? "Damenartikel" : "Herrenartikel" })}
              </PillChip>
            ))}
          </div>
          {facets.gender_source === "AVS" && (
            <p className="mt-1 text-[10px] text-emerald-700">{t("facet_editor.avs_confirmed", { defaultValue: "Via AVS-Verifizierung bestätigt" })}</p>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.age_range")}</label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {AGE_RANGES.map((r) => (
              <PillChip key={r} testId={`facet-age-${r}`} active={facets.age_range === r}
                disabled={locked}
                onClick={() => { if (!locked) setFacets(prev => ({ ...prev, age_range: r })); }}
                variant="neutral" size="md">
                {r}
              </PillChip>
            ))}
          </div>
          {facets.age_range_source === "AVS" && (
            <p className="mt-1 text-[10px] text-emerald-700">{t("facet_editor.avs_confirmed", { defaultValue: "Via AVS-Verifizierung bestätigt" })}</p>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.height_cm")}</label>
          <input data-testid="facet-height" type="number" min="140" max="220" value={facets.height_cm || ""}
            disabled={!otherFieldsEditable && !!facets.height_cm}
            onChange={(e) => setFacets(prev => ({ ...prev, height_cm: e.target.value }))}
            className="mt-1 w-full h-10 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={t("facets.height_ph")} />
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.weight_kg")}</label>
          <input data-testid="facet-weight" type="number" min="40" max="200" value={facets.weight_kg || ""}
            disabled={!otherFieldsEditable && !!facets.weight_kg}
            onChange={(e) => setFacets(prev => ({ ...prev, weight_kg: e.target.value }))}
            className="mt-1 w-full h-10 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={t("facets.weight_ph")} />
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.body_type")}</label>
          <div className="flex gap-2 mt-2 flex-wrap">
            {BODY_TYPE_CODES.map((code) => {
              const dbVal = facets.body_type;
              const dbCode = BODY_TYPE_LEGACY_TO_CODE[dbVal] || dbVal;
              const isActive = dbCode === code;
              const bodyLocked = !otherFieldsEditable && !!dbVal;
              return (
                <PillChip key={code} testId={`facet-body-${code.toLowerCase()}`} active={isActive}
                  disabled={bodyLocked}
                  onClick={() => !bodyLocked && setFacets(prev => ({ ...prev, body_type: code }))} variant="neutral" size="md">
                  {t(`facets.body_type_options.${code}`, {
                    defaultValue: { SLIM: "Schlank", SPORTY: "Sportlich", NORMAL: "Normal", CURVY: "Kurvig", STRONG: "Kräftig" }[code]
                  })}
                </PillChip>
              );
            })}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.shoe_size")}</label>
          <input data-testid="facet-shoe" value={facets.shoe_size || ""}
            disabled={!otherFieldsEditable && !!facets.shoe_size}
            onChange={(e) => setFacets(prev => ({ ...prev, shoe_size: e.target.value }))}
            className="mt-1 w-full h-10 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={t("facets.shoe_ph")} />
        </div>

        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("facets.hair_color")}</label>
          <input data-testid="facet-hair" value={facets.hair_color || ""}
            disabled={!otherFieldsEditable && !!facets.hair_color}
            onChange={(e) => setFacets(prev => ({ ...prev, hair_color: e.target.value }))}
            className="mt-1 w-full h-10 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:opacity-50 disabled:cursor-not-allowed"
            placeholder={t("facets.hair_ph")} />
        </div>

        <button data-testid="facet-save" onClick={saveFacets} disabled={saving}
          className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
          <Save size={14} /> {saving ? t("facets.saving") : t("facets.save")}
        </button>
      </div>
    </div>
  );
}
